import { LiabilityType } from '@ourbranch/lookups';
import { FarmLiabilityCoverageDetails } from '@ourbranch/schema-types/staff-appsync';

/**
 * Issue #30825: Canonical "empty" shape for farmLiabilityCoverage. Used by
 * both staff-fe (clears liability section eagerly when the user toggles it off
 * or switches Personal↔Commercial) and the post-data-rules (defensive backstop
 * when `includeFarmLiability` is false). Keeping a single source of truth
 * stops the FE and BE defaults from drifting as new fields are added to
 * FarmLiabilityCoverageDetails.
 *
 * Only fields with non-undefined defaults are listed. Fields like `type`,
 * coverage limits, etc. are intentionally absent so they read back as
 * undefined / null after a reset.
 *
 * Note: farmInterestedParties is policy-wide and is NOT included here.
 */
export const buildEmptyFarmLiabilityCoverage = (): Partial<FarmLiabilityCoverageDetails> => ({
  recreationalVehicleUMUIM: 'NONE',
  watercraftUMUIM: 'NONE',
  personalInjury: false,
  numManMonths: 0,
  careProvidedToOthers: 0,
  careProvidedToOthersDescription: '',
  hasExclusions: false,
  additionalPropertiesExtensions: [],
  additionalAssetsExtensions: [],
  domesticEmployees: [],
  customFarmWorkItems: [],
  customFeeding: [],
  businessActivities: [],
  businessPursuits: [],
  occupancies: [],
  employees: { fullTime: 0, partTime: 0, partTime40DaysOrLess: 0 }
});

/**
 * Issue #30825 follow-up: defaults the reporter expects to see when the
 * user toggles Liability ON or switches Personal↔Commercial. Carrier-keyed
 * (#33168) to mirror the per-carrier seeds in `packages/data-defaults/src/
 * carriers/{forreston,cfm,oakwood}/defaults/farm-defaults.ts`, which apply
 * these at offer creation, and the `aggregate = coverageL × 2` invariant
 * enforced by each carrier's post-data-rules on save. `personalInjury` is
 * included so a toggle OFF→ON round-trip on a Personal farm offer doesn't
 * silently flip the field from "Included" to "Not included".
 */
// #33674: forreston / cfm / oakwood farm-defaults all seed a $25,000 Limited Farm Pollution limit at
// offer creation, so `farmPollutionLimit` is repeated in every entry below — a toggle OFF→ON / type
// change must not drop it.
const FARM_LIABILITY_DEFAULTS_BY_CARRIER = {
  default: {
    coverageL: 100_000,
    coverageM: 10_000,
    annualAggregateLimit: 200_000,
    personalInjury: true,
    farmPollutionLimit: 25_000
  },
  cfm: {
    coverageL: 300_000,
    coverageM: 10_000,
    annualAggregateLimit: 600_000,
    personalInjury: true,
    farmPollutionLimit: 25_000
  }
} as const;

export const getFarmLiabilityDefaults = (carrier?: string) =>
  FARM_LIABILITY_DEFAULTS_BY_CARRIER[carrier as keyof typeof FARM_LIABILITY_DEFAULTS_BY_CARRIER] ??
  FARM_LIABILITY_DEFAULTS_BY_CARRIER.default;

export const buildDefaultFarmLiabilityCoverage = (
  type: string = LiabilityType.Personal,
  carrier?: string
): Partial<FarmLiabilityCoverageDetails> => {
  const defaults = getFarmLiabilityDefaults(carrier);
  return {
    ...buildEmptyFarmLiabilityCoverage(),
    type,
    coverageL: defaults.coverageL,
    coverageM: defaults.coverageM,
    annualAggregateLimit: defaults.annualAggregateLimit,
    personalInjury: defaults.personalInjury,
    farmPollutionLimit: defaults.farmPollutionLimit
  };
};
